import { createRef } from 'react';
import create, { UseStore } from 'zustand';
import {
  autoCompleteRequest,
  CodeSet,
  CollectionChangeApi,
  collectionChanges,
  currentWorkflow,
  diagnosis,
  Diagnosis,
  filterForCodeset,
  findDxOrProcedures,
  formApi,
  FormApi,
  IcdCptApi,
  isReviewWorkflowState,
  MoveItem,
  reviewActionOr,
  reviewChangesOr,
  truCode,
  updateElementAudit,
  updateIcdLineNumbers,
  utcDateString,
  WorksheetContext,
} from '../../core';

import { inactiveListApi, ListApi, listApi } from '../../core/list';
import { UseNotesApi } from '../common/notes';

type ReasonForVisitProps = {
  context: WorksheetContext;
  icdCpt: IcdCptApi;
};

type ReasonForVisitData = {
  enabled: boolean;
};
export type ReasonForVisitApi = {
  data: UseStore<ReasonForVisitData>;
  context: WorksheetContext;
  icds: ListApi<Diagnosis>;
  form: FormApi<{ code: any; enabled: boolean }>;
  icdChanges: CollectionChangeApi<Diagnosis>;
  inactiveIcds: ListApi<Diagnosis>;
  notesApi: UseNotesApi;
  moveIcd(item: MoveItem): void;
  addIcd(code: CodeSet): void;
  removeIcd(dx: Diagnosis): void;
  setFullText(a: Boolean): void;
  clear(): void;
};

export function reasonForVisitApi({
  context,
  icdCpt,
}: ReasonForVisitProps): ReasonForVisitApi {
  const { worksheet, status, config, readonly, notesApi } = context;

  const workflow = currentWorkflow();
  const isReview = isReviewWorkflowState();

  const defaultFilter = 'active';
  const data = create(() => ({
    enabled: false,
  }));
  const icds = listApi({
    defaultFilter,
    all: worksheet?.reasonForVisit,
    id: (a) => a.diagnosisCode,
    equals: (a, b) => a.diagnosisCode === b.diagnosisCode,
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
    destructive: !isReview,
    allowDuplicates: false,
    postProcess: (v) => updateIcdLineNumbers(v)
  });

  const icdFn = isReview ? icds.actions.all : icds.actions.values;
  icds.events.values(() => {
    icdCpt.setReasonForVisit(icdFn());
  });
  icdCpt.setReasonForVisit(icdFn());

  const { auditCPT } = context.config;
  let icdMoved: Diagnosis[];

  function moveIcd(item: MoveItem) {
    icdMoved = icds.data.getState().values.slice();
    // console.log('move', icdMoved.map((a) => a.diagnosisCode).join(','));
    const el: Diagnosis = icds.data.getState().values[item.currentIndex];

    reviewActionOr(
      {
        title: 'Move Reason ICD ',
        list: auditCPT,
        onComplete: (a) => {
          a.action = 'MOVE';
          a.code = el?.diagnosisCode;
          a.oldLineNumber = item.currentIndex + 1;
          a.newLineNumber = item.newIndex + 1;
          a.workflow = workflow;

          worksheet.diagnosisMoves = worksheet.diagnosisMoves || [];
          worksheet.diagnosisMoves.push(a);

          worksheet.diagnosisMove = el.audit;
          icds.actions.move(item);
          icds.actions.refresh();
        },
      },
      () => {
        icds.actions.move(item);
        icds.actions.refresh();
      }
    );
  }

  let lastCodeSetForTruCode: CodeSet = null;
  let pendingSearch: boolean;
  let input = '';
  let fullText = false;

  icdCpt.onFullTextChanged((a) => (fullText = a));
  
  const initialValues = {
    code: null,
    enabled: false,
  };
  const form: FormApi<{ code: any; enabled: any }> = formApi({
    disabled: readonly,
    values: initialValues,
    fields: {
      enabled: {
        label: '',
        name: 'rfv-enabled',
        tabIndex: -1,
        onChange: (enabled) => {
          data.setState({ enabled });
        },
      },
      code: {
        label: 'ICD',
        name: 'code',
        id: 'rfv-input',
        required: true,
        list: [],
        style: { width: 100 },
        listLabel: (c: CodeSet) =>
          (c &&
            c.description &&
            c.code +
              ' - ' +
              c.codeType +
              ' - ' +
              c.description.longDescription) ||
          '',
        textLabel: (c: CodeSet) => (c && c.code) || '',
        onEnter: () => form.submit(),
        inputRef: createRef(),
        onInputChange: (v: string) => {
          v = v || v.trim();
          if (!v.length) {
            form.controls.code.list.getState().setValue([]);
            return;
          }
          input = v;
          if (v.length >= 2) {
            if (v.indexOf(' ') > -1) {
              const filter = filterForCodeset(lastCodeSetForTruCode);

              truCode.getState().openCodeBooks({
                serviceDate: getSearchDate(), 
                filter,
                query: v.replace(' ', '').trim(),
                events: {
                  onCodePosted: (e) => {
                    if (e.codes && e.codes.length) {
                      form.setValues({}, true);
                      findCode(e.codes[0]);
                    }
                  },
                },
              });
              setCode(null);
            } else {
              search(v);
            }
          }
        },
        onKeyUp: async (v: string) => {},
      },
    },
    valid: (v) => !!inputValue(),
    onSubmit: (v) => findCode(v.code, true),
    resetValues: () => initialValues,
  });

  const { code } = form.controls;

  let inputEl: HTMLInputElement;
  function inputElement(): HTMLInputElement | null {
    if (inputEl) {
      return inputEl;
    }
    const el: HTMLInputElement = code.inputRef.current as any;
    if (el && el.children) {
      inputEl = el.children[1].children[0] as any;
      return inputEl;
    }
    return null;
  }

  async function setCode(v: string | number, doSearch = true) {
    input = (v || '').toString();

    const child: HTMLInputElement = inputElement();
    if (child) {
      code.value.getState().setValue({ code: input });
      child.value = input;
      child.focus();
      child.setSelectionRange(input.length, input.length);
      if (input?.length && doSearch) {
        await search(input);
      } else {
        code.list.getState().setValue([]);
      }
    }
  }

  function inputValue(c?: CodeSet, clearInput = false): string | null {
    const codeSet: CodeSet = c || code.value.getState().value;
    if (codeSet?.code) {
      return codeSet?.code;
    }
    if (input?.length) {
      return input;
    }
    const el = inputElement();
    if (el?.value) {
      const value = el?.value;
      if (clearInput) {
        el.value = '';
      }
      return value;
    }
    return null;
  }

  function getSearchDate() {
    const date: Date =
      icdCpt.form.controls.serviceDate.value.getState().value || new Date();
    return utcDateString(date);
  }

  function setAutoComplete(r: CodeSet[]) {
    r = (r || []).filter((a) => a.codeType === 'ICD_10');
    if (r && r.length) {
      lastCodeSetForTruCode = r[0];
      form.controls.code.list.getState().setValue(r);
    }
  }

  const search = async (v: string) => {
    if (!pendingSearch && v && v.length >= 2) {
      pendingSearch = true;

      let serviceDate = getSearchDate();
      const r: CodeSet[] = await autoCompleteRequest({
        serviceDate,
        fullText,
        code: v,
      });
      setAutoComplete(r);
      pendingSearch = false;
    }
  };

  const findCode = async (c?: Partial<CodeSet>, clearInput = false) => {
    let v = inputValue(c, clearInput);

    // setStatus(null);
    //TODO normalize description location in codeset methods (autocomplete returns description object, vs string here)
    const serviceDate = getSearchDate();

    const { locationCode, departmentCode, worksheetType } = worksheet;
    let errors = '';

    const dx = editingIcd;
    const codeType = c?.codeType || (editingIcd ? 'ICD_10' : undefined);

    async function setCode(v: string | number) {
      input = (v || '').toString();

      const child: HTMLInputElement = inputElement();
      if (child) {
        code.value.getState().setValue({ code: input });
        child.value = input;
        child.focus();
        child.setSelectionRange(input.length, input.length);
      }
    }

    const result = await findDxOrProcedures(
      {
        dx,
        serviceDate,
        codeType,
        locationCode,
        departmentCode,
        worksheetType,
        code: v,
      },
      (e) => (errors = e || 'No result found for ' + v)
    );

    const codes: CodeSet[] = (result || []).map((a: CodeSet) => ({
      ...a,
      ...{
        description: {
          longDescription: a.longDescription,
          shortDescription: a.shortDescription,
          description: a.longDescription,
        },
      },
    }));

    const setStatus = status.getState().setStatus;

    if (codes?.length > 0) {
      if (codes?.length === 1) {
        const codeSet = codes[0];

        if (codeSet.codeType === 'ICD_10') {
          addIcd(codeSet);
        }

        code.value.setState({ value: null });
        setCode('');
      } else {
        setStatus('Multiple results found for ' + v);
        const el = inputElement();
        if (el) {
          el.value = v;
          el.blur();
          setTimeout(() => setCode(v), 10);
        }
      }
    } else {
      setStatus(errors);
    }
    input = '';
    form.setValues({}, true);
  };

  const { auditICD } = config;
  let editingIcd: Diagnosis = null;
  function addIcd(c: CodeSet) {
    const dx = diagnosis(c, workflow);
    const match = (v: Diagnosis) =>
      v.diagnosisCode === editingIcd.diagnosisCode;

    if (editingIcd) {
      reviewChangesOr(
        {
          title: 'Revise Reason ICD ' + dx.diagnosisCode,
          original: editingIcd,
          update: dx,
          changeReasons: {
            diagnosisCode: auditICD,
          },
          fieldNames: ['diagnosisCode'],
          onComplete: (v) => {
            v.action = 'UPDATE';
            updateElementAudit(dx, v);
            icds.actions.replace(dx, match);
            editingIcd = null;
          },
          onCancel: () => {
            editingIcd = null;
          },
        },
        () => {
          icds.actions.replace(dx, match);
          editingIcd = null;
        }
      );
    } else {
      reviewActionOr(
        {
          title: 'Add Reason ICD ' + dx.diagnosisCode,
          list: auditICD,
          onComplete: (v) => {
            v.action = 'CREATE';
            updateElementAudit(dx, v);
            icds.actions.add(dx);
            editingIcd = null;
          },
          onCancel: () => (editingIcd = null),
        },
        () => {
          icds.actions.add(dx);
          editingIcd = null;
        }
      );
    }
  }

  function removeIcd(dx: Diagnosis) {
    function remove() {
      icds.actions.remove(dx);
    }

    if (dx) {
      reviewActionOr(
        {
          title: 'Remove Reason ICD ' + dx.diagnosisCode,
          list: context.config.auditICD,
          onComplete: (v) => {
            v.action = 'DELETE';
            updateElementAudit(dx, v);

            remove();
          },
        },
        () => {
          remove();
        }
      );
    }
  }

  const icdChanges = collectionChanges(icds, {
    changeLabel: (v: Diagnosis[]) => v.map((u) => u.diagnosisCode).join(', '),
  });

  const inactiveIcds = inactiveListApi(icds);

  const setFullText = (a: boolean) => (fullText = a);
  const clear = () => setCode('');
  return {
    data,
    context,
    icds,
    icdChanges,
    inactiveIcds,
    notesApi,
    form,
    moveIcd,
    addIcd,
    removeIcd,
    setFullText,
    clear
  };
}
